import { inr } from "../lib/api";

// Renders a print-only view of a bill. Uses window.print() with a CSS
// @media print rule that hides everything except this component.
export default function PrintableBill({ bill, settings = {} }) {
  const {
    bill_no, created_at, customer_name, customer_phone, items = [],
    subtotal, discount, cgst, sgst, total, paid, balance,
    payments = [], gst_enabled, gst_rate, gstin, notes, cashier_name,
  } = bill;

  return (
    <div className="printable-bill fixed inset-0 z-[9999] bg-white text-black p-8 overflow-auto" data-testid="printable-bill">
      <style>{`
        @media screen {
          .printable-bill { font-family: 'Courier New', monospace; max-width: 500px; margin: 40px auto; box-shadow: 0 6px 40px rgba(0,0,0,0.3); }
        }
        @media print {
          body * { visibility: hidden !important; }
          .printable-bill, .printable-bill * { visibility: visible !important; }
          .printable-bill { position: absolute !important; inset: 0 !important; box-shadow: none !important; padding: 8mm !important; max-width: none !important; }
          @page { margin: 8mm; size: 80mm auto; }
        }
      `}</style>

      <div className="text-center mb-3">
        <img src={settings.logo_url || "/ambrai-logo.png"} alt="Amraiya" className="h-10 w-auto mx-auto mb-1 object-contain" />
        {settings.shop_name && <p className="text-xs font-bold uppercase tracking-widest">{settings.shop_name}</p>}
        {settings.address && <p className="text-xs whitespace-pre-line">{settings.address}</p>}
        {settings.phone && <p className="text-xs">Ph: {settings.phone}</p>}
        {settings.gstin && <p className="text-xs">GSTIN: {settings.gstin}</p>}
      </div>

      <div className="border-t border-b border-black py-1.5 my-3 text-xs">
        <div className="flex justify-between"><span>Bill No</span><span className="font-bold">{bill_no}</span></div>
        <div className="flex justify-between"><span>Date</span><span>{new Date(created_at).toLocaleString("en-IN")}</span></div>
        {cashier_name && <div className="flex justify-between"><span>Cashier</span><span>{cashier_name}</span></div>}
        {customer_name && <div className="flex justify-between"><span>Customer</span><span>{customer_name}</span></div>}
        {customer_phone && <div className="flex justify-between"><span>Phone</span><span>{customer_phone}</span></div>}
        {gstin && <div className="flex justify-between"><span>Cust GSTIN</span><span>{gstin}</span></div>}
      </div>

      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-black">
            <th className="text-left py-1">Item</th>
            <th className="text-center">Qty</th>
            <th className="text-right">Rate</th>
            <th className="text-right">Amt</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} className="align-top">
              <td className="py-1 pr-1">
                {it.name}
                <div className="text-[10px] text-gray-600">
                  {it.sku && `SKU ${it.sku}`}{it.size && ` · Sz ${it.size}`}{it.color && ` · ${it.color}`}
                </div>
              </td>
              <td className="text-center">{it.qty}</td>
              <td className="text-right">{inr(it.unit_price)}</td>
              <td className="text-right">{inr(it.unit_price * it.qty - (it.line_discount || 0))}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t border-black mt-2 pt-2 text-xs space-y-0.5">
        <div className="flex justify-between"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
        {discount > 0 && <div className="flex justify-between"><span>Discount</span><span>− {inr(discount)}</span></div>}
        {gst_enabled && (
          <>
            <div className="flex justify-between"><span>CGST ({gst_rate / 2}%)</span><span>{inr(cgst)}</span></div>
            <div className="flex justify-between"><span>SGST ({gst_rate / 2}%)</span><span>{inr(sgst)}</span></div>
          </>
        )}
        <div className="flex justify-between font-bold text-sm border-t border-black pt-1 mt-1">
          <span>TOTAL</span><span>{inr(total)}</span>
        </div>
      </div>

      {payments.length > 0 && (
        <div className="border-t border-dashed border-black mt-2 pt-2 text-xs">
          {payments.map((p, i) => (
            <div key={i} className="flex justify-between"><span>{(p.mode || "").toUpperCase()}</span><span>{inr(p.amount)}</span></div>
          ))}
          <div className="flex justify-between font-bold"><span>Paid</span><span>{inr(paid)}</span></div>
          {balance > 0 && <div className="flex justify-between text-red-700 font-bold"><span>BALANCE DUE</span><span>{inr(balance)}</span></div>}
        </div>
      )}

      {notes && <p className="text-xs mt-3 pt-2 border-t border-dashed border-black italic">{notes}</p>}

      <p className="text-center text-xs mt-4 pt-2 border-t border-black">
        {settings.footer_line || "Thank you for shopping with us"}
      </p>
      <p className="text-center text-[10px] mt-1">Exchange within 7 days with original bill. No refunds on customised pieces.</p>
    </div>
  );
}
